import {
  Pressable, ScrollView, StyleSheet, Text, TouchableOpacity, View, InteractionManager,
} from 'react-native';
import * as Animatable from 'react-native-animatable';
import { useEffect, useRef, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useDispatch } from 'react-redux';
import Animated, {
  runOnJS, SharedValue, useAnimatedReaction, useAnimatedStyle, useSharedValue,
} from 'react-native-reanimated';
import Colors from '../../constants/Colors';
import Style from '../../constants/Style';
import RightArrow from '../16pxIcons/RightArrow';
import InProgressIcon from '../40pxIcons/InProgressIcon';
import PausedStatusIcon from '../40pxIcons/PausedStatusIcon';
import CompletedIcon from '../40pxIcons/CompletedIcon';
import FlagIcon from '../FlagIcon';
import FlagIcon40 from '../40pxIcons/FlagIcon';
import CloseModalIcon from '../CloseModalIcon';
import getPriorityColor from '../PriorityColorScheme';
import { logEvent } from '../LogEvent';
import {
  DEFAULT_TICKET_PRIORITY, ticketPriorityIsValid, Ticket as TicketType,
} from '../../constants/Types';
import { updateSelectedTicket } from '../../redux/selectedTicketSlice';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import isMobile from '../IsMobile';

interface Props {
  timestamp: any;
  asset?: any;
  showAlert?: any;
  slaNames: string[];
  focusedAssetId?: SharedValue<string | null>;
  assetTickets?: TicketType[];
  selectedAssetId?: string;
  setSelectedAssetId?: any;
  deepLinkTicketId?: string;
  ticketId?: string;
  setModalVisible?: any;
  mapViewWidth?: number;
  mapViewHeight?: number;
}

const Ticket = ({
  asset, slaNames, focusedAssetId, assetTickets,
  setSelectedAssetId, deepLinkTicketId, setModalVisible, mapViewWidth = 0, mapViewHeight = 0,
}: Props) => {
  const navigation = useNavigation();
  const { name } = asset;
  const [infoShown, setInfoShown] = useState<boolean>(false);
  const dispatch = useDispatch();
  const scrollRef = useRef<ScrollView>(null);

  const left = useSharedValue(0);
  const top = useSharedValue(0);

  const ITEM_HEIGHT = 64;

  useEffect(() => {
    if (infoShown && deepLinkTicketId && assetTickets?.length) {
      const index = assetTickets.findIndex((t) => t.id === deepLinkTicketId);
      if (index !== -1) {
        InteractionManager.runAfterInteractions(() => {
          scrollRef.current?.scrollTo({
            y: index * ITEM_HEIGHT,
            animated: true,
          });
        });
      }
    }
  }, [infoShown, deepLinkTicketId, assetTickets]);

  useAnimatedReaction(
    () => focusedAssetId?.value === asset.id,
    (focusedValue, previousFocusedValue) => {
      if (focusedValue !== previousFocusedValue) {
        runOnJS(setInfoShown)(focusedValue);
      }
    },
  );

  const positionStyle = useAnimatedStyle(() => {
    let calculatedLeft = 4;
    let calculatedTop = -44;

    if ((left.value - 300) > mapViewWidth / 2) {
      calculatedLeft = -330;
    }
    if ((top.value - 100) > mapViewHeight / 2) {
      calculatedTop = -260;
    }

    return {
      left: calculatedLeft,
      top: calculatedTop,
      position: 'absolute',
      opacity: 1,
      height: undefined,
      overflow: 'visible',
      zIndex: infoShown ? 10 : 0,
      padding: 16,
    };
  }, [mapViewWidth, mapViewHeight, asset.id]);

  const hideInfo = () => {
    logEvent('Ticket', 'close_modal');
    setInfoShown(false);
  };

  const ticketInfo = () => (
    <Animated.View
      style={[{
        marginHorizontal: 8,
        padding: 16,
        zIndex: -1,
      },
      positionStyle,
      ]}>
      <View style={styles.ticketInfo}>
        <View style={Style.row}>
          <Text style={styles.buttonText}>{name}</Text>
          <TouchableOpacity onPress={hideInfo} testID='map-close-ticket-modal-on-the-map-btn'>
            <CloseModalIcon />
          </TouchableOpacity>
        </View>
        <Text
          style={styles.assetId}
          selectable
        >
          {asset.id}
        </Text>
        <Text
          style={{
            fontFamily: 'Poppins_500Medium',
            fontSize: 14,
            lineHeight: 20,
            marginTop: 8,
            marginBottom: 4,
            color: Colors.light.hereworksBlack500,
          }}
        >
          Tickets
        </Text>
        <ScrollView
          ref={scrollRef}
          style={styles.ticketList}
        >
          {(assetTickets ?? []).map((ticket: TicketType, index: number) => {
            let { priority } = ticket;
            if (!ticketPriorityIsValid(priority)) priority = ticket.priority;
            if (!ticketPriorityIsValid(priority)) priority = DEFAULT_TICKET_PRIORITY;
            return <View style={{
              flexDirection: 'row',
              marginVertical: 8,
              padding: 6,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: deepLinkTicketId === ticket.id
                ? Colors.light.hereworksBlue500
                : Colors.light.hereworksBlue100,
              alignItems: 'center',
              backgroundColor: deepLinkTicketId === ticket.id ? Colors.light.hereworksWhite500 : 'transparent',
            }} key={index}>
              <View>
                <View
                  style={[
                    styles.priorityCircle,
                    { backgroundColor: getPriorityColor(priority) },
                  ]}
                />
                {ticket.status === 'inprogress' && <InProgressIcon style={styles.checkmarkStyle} />}
                {ticket.status === 'paused' && <PausedStatusIcon style={styles.checkmarkStyle} />}
                {ticket.status === 'completed' && <CompletedIcon style={styles.checkmarkStyle} />}
                {ticket.status !== 'inprogress'
                  && ticket.status !== 'paused'
                  && ticket.status !== 'completed' && <FlagIcon40 style={styles.checkmarkStyle} />}
              </View>
              <View style={{ flex: 1 }}>
                <Text
                  style={{
                    flex: 1,
                    fontFamily: 'Roboto_400Regular',
                    fontSize: 14,
                    color: Colors.light.hereworksBlack500,
                  }}
                >
                  {ticket.name}
                </Text>
                {ticketPriorityIsValid(priority) && <Text
                  style={{
                    flex: 1,
                    fontSize: 12,
                    fontFamily: 'Roboto_500Medium',
                    color: Colors.light.hereworksBlack400,
                  }}
                >
                  {`P${priority + 1} - `}
                  {slaNames[priority]}
                </Text>}
              </View>
              <TouchableOpacity
                style={{ padding: 2 }}
                onPress={() => {
                  logEvent('Ticket', 'arrow');
                  const ticketCopy = { ...ticket };
                  const onMap = (ticketCopy.asset as any)?.customFields.some(
                    (field) => field.name === 'GeoX' && Number(field.value) > 0,
                  );
                  ticketCopy.onMap = onMap;
                  ticketCopy.asset = null;
                  ticketCopy.pinned = true;
                  dispatch(updateSelectedTicket({ ...ticketCopy, pinned: true }));
                  navigation.navigate(AppScreen.TICKETS as any);
                }}
                testID='map-navigate-to-ticket-from-map-modal-btn'
              >
                <RightArrow />
              </TouchableOpacity>
            </View>;
          })}
        </ScrollView>
      </View>
    </Animated.View>
  );

  const TicketIcon = ({ style }) => (<View
    style={[
      {
        backgroundColor: infoShown ? '#fff' : Colors.light.hereworksBlue500,
        width: 44,
        height: 44,
        borderRadius: 22,
        borderWidth: 2,
        borderColor: infoShown ? Colors.light.hereworksBlue500 : '#fff',
        alignItems: 'center',
        justifyContent: 'center',
        transform: [
          { translateX: -16 },
          { translateY: -16 },
        ],
      },
      style,
    ]}
  >
    <FlagIcon color={infoShown ? Colors.light.hereworksBlue500 : '#FFF'} />
    {assetTickets?.length > 1 && (
      <View
        style={{
          position: 'absolute',
          top: -4,
          right: -4,
          width: 18,
          height: 18,
          borderRadius: 9,
          backgroundColor: Colors.light.hereworksUrgentRed,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Text style={{
          color: '#fff',
          fontFamily: 'Roboto_700Bold',
          fontSize: 12,
        }}>{assetTickets.length}</Text>
      </View>
    )}
  </View>);

  if (isMobile) {
    return (
      <>
        <Pressable
          style={() => [{
            position: 'absolute',
          }]}
          onPress={() => {
            setModalVisible(true);
            setSelectedAssetId(asset.id);
          }}
        >
          {(state) => (
            <Animatable.View
              style={{
                backgroundColor: 'transparent',
                flexDirection: 'row',
              }}
            >
              <TicketIcon style={{
                backgroundColor: state.pressed
                  ? Colors.light.hereworksBlue100 : Colors.light.hereworksBlue500,
              }} />
              {infoShown && ticketInfo()}
            </Animatable.View>
          )}
        </Pressable>
      </>
    );
  }

  return (
    <View style={{ position: 'absolute' }}>
      <Animatable.View
        style={{
          backgroundColor: 'transparent',
          flexDirection: 'row',
        }}
        animation={'fadeIn'}
        onPointerEnter={(event) => {
          if (!infoShown) {
            left.value = event.nativeEvent.x;
            top.value = event.nativeEvent.y;
          }
        }}
      >
        <TicketIcon style={{ opacity: 1 }} />
        {infoShown && ticketInfo()}
      </Animatable.View>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonText: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 16,
    color: Colors.light.hereworksBlack600,
  },
  priorityCircle: {
    position: 'absolute',
    top: 2,
    left: 2,
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 3,
    borderColor: '#fff',
  },
  checkmarkStyle: {
    marginRight: 20,
  },
  assetId: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    lineHeight: 20,
    color: Colors.light.hereworksBlack400,
  },
  ticketList: {
    maxHeight: 160,
    paddingEnd: 16,
    marginEnd: -16,
  },
  ticketInfo: {
    backgroundColor: '#fff',
    width: 280,
    marginHorizontal: 8,
    padding: 16,
    borderRadius: 10,
    cursor: 'auto',
    borderWidth: isMobile ? 0 : 1,
    borderColor: Colors.light.hereworksBlue100,
  },
});

export default Ticket;
